import React, { useContext, useEffect, useState } from 'react'
import API from '../../services/API'
import {
  Alert,
  Autocomplete,
  Button,
  Drawer,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CustomDataGrid from '../../components/DataGrid/CustomDataGrid'
import InfoIcon from '@mui/icons-material/Info'
import { DetailRow } from '../../components/Cards/DetailsBox'
import { GlobalContext } from '../../App'
import SmallModal from '../../components/Modals/SmallModal'

const PendingClientAccounts = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState(null)

  const [listOfOrganisations, setListOfOrganisations] = useState([])

  const [assignedOrganisation, setAssignedOrganisation] = useState(null)

  const [openConfirmReject, setOpenConfirmReject] = useState(false)

  const { setToast } = useContext(GlobalContext)
  const loadData = () => {
    setLoading(true)
    API.get('/client-portal/pending-accounts')
      .then((res) => {
        if (res.status === 200) {
          console.log(res.data)
          setAccounts(res.data)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => loadData(), [])

  useEffect(() => {
    API.get('/event-org-options').then((res) => {
      if (res.status === 200) {
        console.log(res.data)
        setListOfOrganisations(res.data)
      }
    })
  }, [])

  const handleApprove = (e) => {
    e.preventDefault()
    API.post('/contact-approval', {
      clientContactID: selectedAccount._id,
      organisationID: assignedOrganisation._id,
      orgName: assignedOrganisation.orgName,
    }).then((res) => {
      if (res.status === 200) {
        setSelectedAccount(null)
        setAssignedOrganisation(null)
        loadData()
        setToast({
          message: 'Client Account Approved',
        })
      }
    })
  }

  const handleReject = () => {
    API.post('/contact-rejection', {
      clientContactID: selectedAccount._id,
    }).then((res) => {
      if (res.status === 200) {
        setOpenConfirmReject(false)
        setSelectedAccount(null)
        setAssignedOrganisation(null)
        loadData()
        setToast({ message: 'Client Account Rejected' })
      }
    })
  }

  return (
    <Stack gap={2}>
      <Typography variant="title2">Pending Client Accounts</Typography>
      <CustomDataGrid
        slots={{
          toolbar: () => null,
          footer: () => null,
        }}
        loading={loading}
        rows={accounts}
        getRowId={(row) => row._id}
        columns={[
          {
            field: 'name',
            headerName: 'Name',
            minWidth: 200,
            flex: 0.8,
            valueGetter: (value, row) =>
              `${row?.userAccount?.firstName} ${row?.userAccount?.lastName}`,
          },
          {
            field: 'orgNameOnApplication',
            headerName: 'Organisation',
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'companyRole',
            headerName: 'Role',
            minWidth: 150,
          },
        ]}
        disableRowSelectionOnClick
        onRowClick={({ row }) => setSelectedAccount(row)}
      />
      <Drawer
        open={!!selectedAccount}
        anchor="right"
        onClose={() => setSelectedAccount(null)}
      >
        <Stack
          gap={2}
          width="40rem"
          padding={2}
          maxWidth="80vw"
          component="form"
          onSubmit={handleApprove}
        >
          <Stack>
            <Typography variant="title2">
              {selectedAccount?.userAccount?.firstName}{' '}
              {selectedAccount?.userAccount?.lastName}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="title3">
                {selectedAccount?.orgNameOnApplication}
              </Typography>
              <Tooltip title="This is the name of the organisation as written during their application">
                <InfoIcon fontSize="small" color="info" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack gap={1}>
            <DetailRow
              label="Job Title"
              value={selectedAccount?.orgNameOnApplication}
            />
            <DetailRow label="Phone Number" value={selectedAccount?.phone} />
            <DetailRow
              label="Email"
              value={selectedAccount?.userAccount?.email}
            />
          </Stack>
          <Autocomplete
            value={assignedOrganisation}
            options={listOfOrganisations}
            getOptionLabel={(option) => option.orgName}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            onChange={(e, value) => setAssignedOrganisation(value)}
            renderInput={(params) => (
              <TextField {...params} label="Assign to Organisation" required />
            )}
          />
          {assignedOrganisation && (
            <Alert severity="warning">
              Please ensure you're selecting the correct Organisation. Once
              submitted, this will allow the above user to access sensitive
              information about that organisation.
            </Alert>
          )}
          <Button type="submit" variant="contained" color="success" fullWidth>
            Approve
          </Button>
          <Button
            type="button"
            variant="contained"
            color="error"
            fullWidth
            onClick={() => setOpenConfirmReject(true)}
          >
            Reject
          </Button>
        </Stack>
      </Drawer>
      <SmallModal
        open={openConfirmReject}
        onClose={() => setOpenConfirmReject(false)}
      >
        <Stack gap={1}>
          <Typography textAlign="center">
            Are you sure you want to reject this user account?
          </Typography>
          <Typography variant="caption" textAlign="center">
            This will remove their user account entirely
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleReject()}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenConfirmReject(false)}
          >
            No
          </Button>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default PendingClientAccounts
